import { useState } from 'react';
import { AvatarLetter } from 'components/avatar';
import Checkbox from 'components/checkbox';
import { USER_PICKER_COPY } from 'constants/invites';
import SearchPicker from 'pages/Users/components/InviteUsers/AddByLink/SearchPicker';
import {
  NoTeamsFound as NoOptions,
  TeamOption as UserOption,
  TeamOptionLabel as UserOptionLabel,
  TeamOptionList as UserOptionList,
} from 'pages/Users/components/InviteUsers/AddByLink/styles';
import { NameEmailCell } from 'pages/Users/components/InviteUsers/AdjustUsersSettings/styles';
import { userEmail, userFullName } from './helpers';
import { IUserPicker } from './types';
import { UserPickerInput } from './styles';

const PANEL_ID = 'team-invite-user-options';

// Searchable list of users not in the team.
const UserPicker: React.FC<IUserPicker> = ({
  users,
  status,
  selectedIds,
  onToggleUser,
  managedTeamsOnly,
}) => {
  const [query, setQuery] = useState('');

  const search = query.trim().toLowerCase();
  const options = users.filter(
    (user) =>
      !search ||
      userFullName(user).toLowerCase().includes(search) ||
      userEmail(user)?.toLowerCase().includes(search)
  );

  const emptyText =
    (status === 'loading' && USER_PICKER_COPY.LOADING) ||
    (status === 'failed' && USER_PICKER_COPY.FAILED) ||
    (users.length === 0 &&
      (managedTeamsOnly
        ? USER_PICKER_COPY.NO_TEAM_USERS_TO_ADD
        : USER_PICKER_COPY.EVERYONE_ON_TEAM)) ||
    USER_PICKER_COPY.NO_USERS;

  return (
    <SearchPicker
      panelId={PANEL_ID}
      label={USER_PICKER_COPY.LABEL}
      searchLabel={USER_PICKER_COPY.SEARCH_LABEL}
      placeholder={USER_PICKER_COPY.PLACEHOLDER}
      input={UserPickerInput}
      chevronSize="small"
      query={query}
      onQueryChange={setQuery}
    >
      {status !== 'ready' || options.length === 0 ? (
        <NoOptions>{emptyText}</NoOptions>
      ) : (
        <UserOptionList>
          {options.map((user) => {
            const name = userFullName(user);
            const email = userEmail(user);
            return (
              <UserOption key={user.id}>
                <Checkbox
                  aria-label={name ? `${name} (${email})` : email}
                  checked={selectedIds.includes(user.id)}
                  controlled
                  onChange={() => onToggleUser(user)}
                  label={
                    <UserOptionLabel>
                      {/* Decorative: the tick box is already named after the user. */}
                      <span aria-hidden="true">
                        <AvatarLetter
                          avatar={user.uaa_data?.profile?.data?.avatar}
                          name={name || email}
                          email={email}
                          size="invite"
                          avatarSize="invite"
                          tinted
                        />
                      </span>
                      {/* A span, as the tick box's label only takes inline content. */}
                      <NameEmailCell as="span">
                        <b>{name || email}</b>
                        {name && <span>{email}</span>}
                      </NameEmailCell>
                    </UserOptionLabel>
                  }
                />
              </UserOption>
            );
          })}
        </UserOptionList>
      )}
    </SearchPicker>
  );
};

export default UserPicker;
